import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquareText,
  Target,
  Sparkles,
  Chrome,
  Calendar,
  Check,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import Header from '@/components/public/Header';
import Footer from '@/components/public/Footer';

const BRAND = '#00d68f';

const FEATURES = [
  {
    icon: MessageSquareText,
    title: 'AI Comments',
    body: 'Generate engaging comments on any LinkedIn post in 5 seconds.',
  },
  {
    icon: Target,
    title: 'Prospect Scorer',
    body: 'Know instantly if someone is worth reaching out to.',
  },
  {
    icon: Sparkles,
    title: 'Post Generator',
    body: 'Live trending topics and posts in your exact voice.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: '30 day trial',
    headline: 'AI Comments only',
    features: ['AI Comments only', '30 day trial', 'Powered by our API — no key needed'],
    cta: 'Install free',
    ctaHref: 'https://chrome.google.com/webstore',
    highlight: false,
  },
  {
    name: 'Pro',
    price: '$100',
    period: 'one time',
    headline: 'AI Comments unlimited',
    features: [
      'AI Comments unlimited',
      'Bring your own Anthropic key',
      'No scorer, no DM, no post generator',
    ],
    cta: 'Get Pro',
    ctaHref: '/linkedin-ai/contact',
    highlight: false,
  },
  {
    name: 'Custom',
    price: '$300',
    period: 'one time',
    headline: 'Everything in Pro · branded for you',
    features: [
      'Everything in Pro',
      'Prospect Scorer',
      'DM Generator',
      'Post Generator',
      'Your logo and branding',
      'Your ICP configured',
      'CRM integration (Asana included)',
      'Onboarding call',
    ],
    cta: 'Book a call',
    ctaHref: '/linkedin-ai/contact',
    highlight: true,
  },
  {
    name: 'Power',
    price: '$500',
    period: '/month',
    headline: 'Custom + outbound at scale',
    features: [
      'Everything in Custom',
      'Email sending (Gmail and Google Workspace)',
      'Apollo contact enrichment',
    ],
    cta: 'Book a call',
    ctaHref: '/linkedin-ai/contact',
    highlight: false,
  },
];

const LinkedinAIPage = () => {
  useEffect(() => {
    document.title = 'LinkedIn AI for serious founders — StartEase AI';
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="linkedin-ai-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 20%, ${BRAND}33, transparent 50%), radial-gradient(circle at 80% 60%, ${BRAND}22, transparent 60%)`,
          }}
        />
        <div className="relative container-custom py-20 md:py-28 text-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{ background: `${BRAND}1a`, color: BRAND, border: `1px solid ${BRAND}55` }}
          >
            <Chrome className="w-3.5 h-3.5" /> Chrome Extension
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl mx-auto leading-tight" data-testid="linkedin-hero-title">
            LinkedIn AI for serious founders
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto" data-testid="linkedin-hero-subtitle">
            AI comments that start conversations. Prospect scoring that saves hours.
            Posts that build your audience.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://chrome.google.com/webstore"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-install-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold text-slate-900 transition-colors"
              style={{ background: BRAND }}
              onMouseOver={(e) => (e.currentTarget.style.background = '#00c181')}
              onMouseOut={(e) => (e.currentTarget.style.background = BRAND)}
            >
              <Chrome className="w-5 h-5" /> Install free — Chrome Web Store
            </a>
            <Link
              to="/linkedin-ai/contact"
              data-testid="hero-book-call-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium text-white border border-white/20 hover:bg-white/10 transition-colors"
            >
              <Calendar className="w-5 h-5" /> Book a strategy call
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-400 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Your LinkedIn data stays in your browser — read locally, never stored.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white" data-testid="linkedin-features">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="bg-slate-50 border border-slate-200 rounded-xl p-7 hover:border-slate-300 transition-colors"
                data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
                  style={{ background: `${BRAND}1a`, color: BRAND }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-slate-50" data-testid="linkedin-pricing">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Simple, founder-friendly pricing</h2>
            <p className="text-slate-600 max-w-xl mx-auto">One-time. No subscriptions. Start free.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl p-7 border ${plan.highlight ? 'shadow-lg' : 'border-slate-200'}`}
                style={plan.highlight ? { borderColor: BRAND, boxShadow: `0 8px 24px ${BRAND}22` } : {}}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.highlight && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-slate-900"
                    style={{ background: BRAND }}
                  >
                    Most popular
                  </span>
                )}
                <h3 className="text-xl font-semibold text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{plan.headline}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-sm text-slate-500 ml-2">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-7">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {plan.ctaHref.startsWith('http') ? (
                  <a
                    href={plan.ctaHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    className={`block text-center px-5 py-2.5 rounded-md font-medium transition-colors ${
                      plan.highlight ? 'text-slate-900' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                    style={plan.highlight ? { background: BRAND } : {}}
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <Link
                    to={plan.ctaHref}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    className={`block text-center px-5 py-2.5 rounded-md font-medium transition-colors ${
                      plan.highlight ? 'text-slate-900' : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                    style={plan.highlight ? { background: BRAND } : {}}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-slate-900 text-center" data-testid="linkedin-bottom-cta">
        <div className="container-custom">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Start using LinkedIn the smart way</h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-8">
            Install in one click. Free trial. Cancel anytime — no card needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://chrome.google.com/webstore"
              target="_blank"
              rel="noreferrer"
              data-testid="bottom-install-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold text-slate-900 transition-colors"
              style={{ background: BRAND }}
            >
              <Chrome className="w-5 h-5" /> Install free
            </a>
            <Link
              to="/linkedin-ai/contact"
              data-testid="bottom-book-call-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium text-white border border-white/20 hover:bg-white/10 transition-colors"
            >
              Book a call <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-400">
            By installing you agree to our <Link to="/linkedin-ai/privacy" className="underline hover:text-white">privacy policy</Link>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LinkedinAIPage;
